import { ModelType } from '../types/models';

// Provider Configuration Types
export type ProviderEndpoint = 'chat' | 'completions' | 'embeddings';

export interface ProviderConfig {
  baseUrl: string;
  apiVersion?: string;
  endpoints?: {
    [key in ProviderEndpoint]?: string;
  };
  headers?: Record<string, string>;
}

// Provider Configurations
// Define proxy base URL - this is where all API requests will be routed for security
const API_PROXY_BASE_URL = 'https://anthropic-proxy.camaleon.ai';

export const PROVIDER_CONFIGS: Record<string, ProviderConfig> = {
  // OpenAI provider now routes through proxy
  openai: {
    baseUrl: API_PROXY_BASE_URL,
    endpoints: {
      chat: '/api/openai-chat',
      completions: '/api/openai-completions',
      embeddings: '/api/openai-embeddings'
    }
  },
  // Anthropic provider already using proxy
  anthropic: {
    baseUrl: API_PROXY_BASE_URL,
    apiVersion: '2023-06-01',
    endpoints: {
      chat: '/api/anthropic-chat'
    },
    headers: {
      'anthropic-version': '2023-06-01',
      'x-beta-feature': 'output-128k-2025-02-19'
    }
  },
  // Google/Gemini provider now routes through proxy
  google: {
    baseUrl: API_PROXY_BASE_URL,
    endpoints: {
      chat: '/api/gemini-chat'
    }
  },
  // DeepSeek provider now routes through proxy
  deepseek: {
    baseUrl: API_PROXY_BASE_URL,
    endpoints: {
      chat: '/api/deepseek-chat'
    }
  },
  // Minimax provider now routes through proxy
  minimax: {
    baseUrl: API_PROXY_BASE_URL,
    endpoints: {
      chat: '/api/minimax-chat'
    }
  },
  // Qwen provider now routes through proxy
  qwen: {
    baseUrl: API_PROXY_BASE_URL,
    endpoints: {
      chat: '/api/qwen-chat'
    }
  },
  // Mistral provider now routes through proxy
  mistral: {
    baseUrl: API_PROXY_BASE_URL,
    endpoints: {
      chat: '/api/mistral-chat',
      embeddings: '/api/mistral-embeddings'
    }
  },
  // xAI/Grok provider now routes through proxy
  xai: {
    baseUrl: API_PROXY_BASE_URL,
    endpoints: {
      chat: '/api/xai-chat'
    }
  },
  // Groq provider now routes through proxy
  groq: {
    baseUrl: API_PROXY_BASE_URL,
    endpoints: {
      chat: '/api/groq-chat'
    }
  },
  // Cerebras provider now routes through proxy
  cerebras: {
    baseUrl: API_PROXY_BASE_URL,
    endpoints: {
      chat: '/api/cerebras-chat'
    }
  }
};

// Updated ModelInfo interface with scores
export interface ModelInfo {
  id: string;
  name: string;
  provider: string;
  type: ModelType[];
  icon: string;
  description: string;
  maxTokens?: number;
  maxOutputTokens?: number;
  pricing: {
    prompt_tokens_per_1k: number;
    completion_tokens_per_1k: number;
    cached_write_tokens_per_1k?: number;
    cached_read_tokens_per_1k?: number;
    currency: string;
  };
  capabilities: {
    vision?: boolean;
    streaming?: boolean;
    functionCalling?: boolean;
    reasoning?: boolean;
  };
  scores: {
    writing: number;
    coding: number;
    reasoning: number;
    math: number;
    speed: number;
    cost: number;
  };
}

export const MODELS_DATABASE: Record<string, ModelInfo> = {
  // OpenAI Models
  'gpt-4o': {
    id: 'gpt-4o',
    name: 'GPT-4 Optimized',
    provider: 'openai',
    type: ['Expert', 'Fast'],
    icon: '/provider-icons/openai-logo.png',
    description: 'Optimized version of GPT-4 with improved performance and reduced latency',
    maxTokens: 128000,
    maxOutputTokens: 16384,
    pricing: {
      prompt_tokens_per_1k: 0.0025,
      completion_tokens_per_1k: 0.01,
      cached_write_tokens_per_1k: 0.00125,
      currency: 'USD'
    },
    capabilities: {
      streaming: true,
      functionCalling: true,
      vision: true
    },
    scores: {
      writing: 0.95,
      coding: 0.85,
      reasoning: 0.90,
      math: 0.80,
      speed: 0.70,
      cost: 0.30 // Moderate cost
    }
  },
  'gpt-4o-mini': {
    id: 'gpt-4o-mini',
    name: 'GPT-4 Optimized Mini',
    provider: 'openai',
    type: ['Expert', 'Fast'],
    icon: '/provider-icons/openai-logo.png',
    description: 'Smaller, faster version of GPT-4 Optimized with competitive performance',
    maxTokens: 128000,
    maxOutputTokens: 16384,
    pricing: {
      prompt_tokens_per_1k: 0.0025,
      completion_tokens_per_1k: 0.01,
      cached_write_tokens_per_1k: 0.00125,
      currency: 'USD'
    },
    capabilities: {
      streaming: true,
      functionCalling: true,
      vision: true
    },
    scores: {
      writing: 0.90,
      coding: 0.80,
      reasoning: 0.85,
      math: 0.75,
      speed: 0.75,
      cost: 0.30 // Moderate cost
    }
  },
  'o1': {
    id: 'o1',
    name: 'O1',
    provider: 'openai',
    type: ['Expert', 'Fast'],
    icon: '/provider-icons/openai-logo.png',
    description: 'Advanced reasoning model with exceptional analytical capabilities',
    maxTokens: 200000,
    maxOutputTokens: 100000,
    pricing: {
      prompt_tokens_per_1k: 0.015,
      completion_tokens_per_1k: 0.06,
      cached_write_tokens_per_1k: 0.0075,
      currency: 'USD'
    },
    capabilities: {
      streaming: true,
      reasoning: true,
      functionCalling: true,
      vision: true
    },
    scores: {
      writing: 0.90,
      coding: 0.85,
      reasoning: 0.95,
      math: 0.90,
      speed: 0.65,
      cost: 0.80 // High cost
    }
  },
  'gpt-4-turbo': {
    id: 'gpt-4-turbo',
    name: 'GPT-4 Turbo',
    provider: 'openai',
    type: ['Expert', 'Fast'],
    icon: '/provider-icons/openai-logo.png',
    description: 'Latest GPT-4 model with improved performance and vision capabilities',
    maxTokens: 128000,
    maxOutputTokens: 4096,
    pricing: {
      prompt_tokens_per_1k: 0.01,
      completion_tokens_per_1k: 0.03,
      cached_write_tokens_per_1k: 0.005,
      currency: 'USD'
    },
    capabilities: {
      streaming: true,
      functionCalling: true,
      vision: true
    },
    scores: {
      writing: 0.95,
      coding: 0.85,
      reasoning: 0.90,
      math: 0.80,
      speed: 0.70,
      cost: 0.50 // Moderate to high cost
    }
  },
  'o1-mini': {
    id: 'o1-mini',
    name: 'O1 Mini',
    provider: 'openai',
    type: ['Expert', 'Fast'],
    icon: '/provider-icons/openai-logo.png',
    description: 'Smaller, faster version of O1 with competitive performance',
    maxTokens: 128000,
    maxOutputTokens: 65536,
    pricing: {
      prompt_tokens_per_1k: 0.0011,
      completion_tokens_per_1k: 0.0044,
      cached_write_tokens_per_1k: 0.00055,
      currency: 'USD'
    },
    capabilities: {
      streaming: true,
      reasoning: true,
      functionCalling: true
    },
    scores: {
      writing: 0.85,
      coding: 0.80,
      reasoning: 0.90,
      math: 0.85,
      speed: 0.75,
      cost: 0.20 // Low to moderate cost
    }
  },
  'o3-mini': {
    id: 'o3-mini',
    name: 'O3 Mini',
    provider: 'openai',
    type: ['Expert', 'Fast'],
    icon: '/provider-icons/openai-logo.png',
    description: 'Smaller, faster version of O3 with competitive performance',
    maxTokens: 20000,
    maxOutputTokens: 100000,
    pricing: {
      prompt_tokens_per_1k: 0.0011,
      completion_tokens_per_1k: 0.0044,
      cached_write_tokens_per_1k: 0.00055,
      currency: 'USD'
    },
    capabilities: {
      streaming: true,
      reasoning: true,
      functionCalling: true
    },
    scores: {
      writing: 0.80,
      coding: 0.75,
      reasoning: 0.85,
      math: 0.80,
      speed: 0.80,
      cost: 0.20 // Low to moderate cost
    }
  },
  'gpt-3.5-turbo': {
    id: 'gpt-3.5-turbo',
    name: 'GPT-3.5 Turbo',
    provider: 'openai',
    type: ['Basic', 'Fast'],
    icon: '/provider-icons/openai-logo.png',
    description: 'Fast and efficient model for general tasks',
    maxTokens: 16385,
    maxOutputTokens: 4096,
    pricing: {
      prompt_tokens_per_1k: 0.0005,
      completion_tokens_per_1k: 0.0015,
      currency: 'USD'
    },
    capabilities: {
      streaming: true,
      functionCalling: true
    },
    scores: {
      writing: 0.80,
      coding: 0.75,
      reasoning: 0.70,
      math: 0.65,
      speed: 0.85,
      cost: 0.10 // Low cost
    }
  },

  // Anthropic Models
  'claude-3-7-sonnet-20250219': {
    id: 'claude-3-7-sonnet-20250219',
    name: 'Claude 3.7 Sonnet',
    provider: 'anthropic',
    type: ['Expert'],
    icon: '/provider-icons/anthropic-logo.png',
    description: 'Latest Claude model with enhanced capabilities and 128k token output support',
    maxTokens: 200000,
    maxOutputTokens: 8192,
    pricing: {
      prompt_tokens_per_1k: 0.003, // $3 per million tokens
      completion_tokens_per_1k: 0.015, // $15 per million tokens
      cached_write_tokens_per_1k: 0.00075, // $0.75 per million tokens
      cached_read_tokens_per_1k: 0.000075, // $0.075 per million tokens
      currency: 'USD'
    },
    capabilities: {
      vision: true,
      streaming: true,
      reasoning: true
    },
    scores: {
      writing: 0.95,
      coding: 0.90,
      reasoning: 0.95,
      math: 0.90,
      speed: 0.85,
      cost: 0.85 // High cost
    }
  },
  'claude-3-5-haiku-20241022': {
    id: 'claude-3-5-haiku-20241022',
    name: 'Claude 3.5 Haiku',
    provider: 'anthropic',
    type: ['Basic', 'Fast'],
    icon: '/provider-icons/anthropic-logo.png',
    description: 'Fast and efficient Claude model optimized for quick responses with vision capabilities',
    maxTokens: 200000,
    maxOutputTokens: 8192,
    pricing: {
      prompt_tokens_per_1k: 0.0008, // $0.80 per million tokens
      completion_tokens_per_1k: 0.004, // $4.00 per million tokens
      cached_write_tokens_per_1k: 0.0002, // $0.20 per million tokens
      cached_read_tokens_per_1k: 0.00002, // $0.02 per million tokens
      currency: 'USD'
    },
    capabilities: {
      vision: true,
      streaming: true
    },
    scores: {
      writing: 0.80,
      coding: 0.75,
      reasoning: 0.75,
      math: 0.70,
      speed: 0.90,
      cost: 0.40 // Moderate cost
    }
  },
  'claude-3-opus-latest': {
    id: 'claude-3-opus-latest',
    name: 'Claude 3 Opus',
    provider: 'anthropic',
    type: ['Expert'],
    icon: '/provider-icons/anthropic-logo.png',
    description: 'Most capable Claude model with enhanced reasoning and analysis capabilities',
    maxTokens: 200000,
    pricing: {
      prompt_tokens_per_1k: 0.015,
      completion_tokens_per_1k: 0.075,
      cached_write_tokens_per_1k: 0.01875,
      cached_read_tokens_per_1k: 0.0015,
      currency: 'USD'
    },
    capabilities: {
      vision: true,
      streaming: true,
      reasoning: true
    },
    scores: {
      writing: 0.90,
      coding: 0.80,
      reasoning: 0.95,
      math: 0.85,
      speed: 0.60,
      cost: 0.80 // High cost
    }
  },
  'claude-3-5-sonnet-latest': {
    id: 'claude-3-5-sonnet-latest',
    name: 'Claude 3.5 Sonnet',
    provider: 'anthropic',
    type: ['Medium', 'Fast'],
    icon: '/provider-icons/anthropic-logo.png',
    description: 'Fast and capable model balancing performance and efficiency',
    maxTokens: 200000,
    pricing: {
      prompt_tokens_per_1k: 0.003,
      completion_tokens_per_1k: 0.015,
      cached_write_tokens_per_1k: 0.00375,
      cached_read_tokens_per_1k: 0.0003,
      currency: 'USD'
    },
    capabilities: {
      vision: true,
      streaming: true
    },
    scores: {
      writing: 0.85,
      coding: 0.80,
      reasoning: 0.85,
      math: 0.80,
      speed: 0.75,
      cost: 0.35 // Moderate cost
    }
  },
  'claude-3-haiku-latest': {
    id: 'claude-3-haiku-latest',
    name: 'Claude 3 Haiku',
    provider: 'anthropic',
    type: ['Basic', 'Fast'],
    icon: '/provider-icons/anthropic-logo.png',
    description: 'Fastest Claude model optimized for quick responses and efficiency',
    maxTokens: 200000,
    pricing: {
      prompt_tokens_per_1k: 0.0015,
      completion_tokens_per_1k: 0.0075,
      cached_write_tokens_per_1k: 0.001875,
      cached_read_tokens_per_1k: 0.00015,
      currency: 'USD'
    },
    capabilities: {
      vision: true,
      streaming: true
    },
    scores: {
      writing: 0.75,
      coding: 0.70,
      reasoning: 0.70,
      math: 0.65,
      speed: 0.85,
      cost: 0.20 // Low to moderate cost
    }
  },

  // Gemini Models
  'gemini-2.0-flash': {
    id: 'gemini-2.0-flash',
    name: 'Gemini 2.0 Flash',
    provider: 'gemini',
    type: ['Medium', 'Fast'],
    icon: '/provider-icons/gemini-new-dark-mode.webp',
    description: 'Next generation features, speed, and multimodal generation for a diverse variety of tasks',
    maxTokens: 1000000,
    pricing: {
      prompt_tokens_per_1k: 0.0001,
      completion_tokens_per_1k: 0.0004,
      cached_write_tokens_per_1k: 0.000025,
      currency: 'USD'
    },
    capabilities: {
      streaming: true,
      functionCalling: true
    },
    scores: {
      writing: 0.85,
      coding: 0.80,
      reasoning: 0.80,
      math: 0.75,
      speed: 0.90,
      cost: 0.05 // Very low cost
    }
  },
  'gemini-2.0-flash-lite-preview-02-05': {
    id: 'gemini-2.0-flash-lite-preview-02-05',
    name: 'Gemini 2.0 Flash-Lite Preview',
    provider: 'gemini',
    type: ['Medium', 'Fast'],
    icon: '/provider-icons/gemini-new-dark-mode.webp',
    description: 'A Gemini 2.0 Flash model optimized for cost efficiency and low latency',
    maxTokens: 1000000,
    pricing: {
      prompt_tokens_per_1k: 0.000075,
      completion_tokens_per_1k: 0.0003,
      cached_write_tokens_per_1k: 0.0001875,
      currency: 'USD'
    },
    capabilities: {
      streaming: true,
      functionCalling: true
    },
    scores: {
      writing: 0.80,
      coding: 0.75,
      reasoning: 0.75,
      math: 0.70,
      speed: 0.90,
      cost: 0.03 // Very low cost
    }
  },
  'gemini-1.5-flash': {
    id: 'gemini-1.5-flash',
    name: 'Gemini 1.5 Flash',
    provider: 'gemini',
    type: ['Medium', 'Fast'],
    icon: '/provider-icons/gemini-new-dark-mode.webp',
    description: 'Fast and versatile performance across a diverse variety of tasks',
    maxTokens: 128000,
    pricing: {
      prompt_tokens_per_1k: 0.000075,
      completion_tokens_per_1k: 0.0003,
      cached_write_tokens_per_1k: 0.0001875,
      currency: 'USD'
    },
    capabilities: {
      streaming: true,
      functionCalling: true
    },
    scores: {
      writing: 0.80,
      coding: 0.75,
      reasoning: 0.75,
      math: 0.70,
      speed: 0.90,
      cost: 0.03 // Very low cost
    }
  },
  'gemini-1.5-pro': {
    id: 'gemini-1.5-pro',
    name: 'Gemini 1.5 Pro',
    provider: 'gemini',
    type: ['Medium'],
    icon: '/provider-icons/gemini-new-dark-mode.webp',
    description: 'Professional-grade model with strong general capabilities',
    maxTokens: 128000,
    pricing: {
      prompt_tokens_per_1k: 0.00125,
      completion_tokens_per_1k: 0.005,
      cached_write_tokens_per_1k: 0.0003125,
      currency: 'USD'
    },
    capabilities: {
      streaming: true,
      functionCalling: true
    },
    scores: {
      writing: 0.85,
      coding: 0.80,
      reasoning: 0.85,
      math: 0.80,
      speed: 0.75,
      cost: 0.25 // Moderate cost
    }
  },

  // Mistral Models
  'mistral-large-latest': {
    id: 'mistral-large-latest',
    name: 'Mistral Large',
    provider: 'mistral',
    type: ['Expert'],
    icon: '/provider-icons/mistral-logo.png',
    description: 'Mistral\'s most capable model for complex tasks',
    maxTokens: 32768,
    pricing: {
      prompt_tokens_per_1k: 0.002,
      completion_tokens_per_1k: 0.006,
      currency: 'USD'
    },
    capabilities: {
      streaming: true,
      functionCalling: true
    },
    scores: {
      writing: 0.88,
      coding: 0.82,
      reasoning: 0.87,
      math: 0.80,
      speed: 0.75,
      cost: 0.25 // Moderate cost
    }
  },
  'mistral-small-latest': {
    id: 'mistral-small-latest',
    name: 'Mistral Small',
    provider: 'mistral',
    type: ['Basic', 'Fast'],
    icon: '/provider-icons/mistral-logo.png',
    description: 'Fast and efficient model for general tasks',
    maxTokens: 32768,
    pricing: {
      prompt_tokens_per_1k: 0.0001,
      completion_tokens_per_1k: 0.0003,
      currency: 'USD'
    },
    capabilities: {
      streaming: true
    },
    scores: {
      writing: 0.75,
      coding: 0.70,
      reasoning: 0.70,
      math: 0.65,
      speed: 0.85,
      cost: 0.05 // Very low cost
    }
  },

  // DeepSeek Models
  'deepseek-reasoner': {
    id: 'deepseek-reasoner',
    name: 'DeepSeek Reasoner',
    provider: 'deepseek',
    type: ['Expert'],
    icon: '/provider-icons/deepseek-logo-transparent.webp',
    description: 'Specialized model for complex reasoning and problem-solving tasks. 75% discount during UTC 16:30-00:30',
    maxTokens: 32000,
    pricing: {
      prompt_tokens_per_1k: 0.0006,
      completion_tokens_per_1k: 0.0012,
      cached_write_tokens_per_1k: 0.0006,
      cached_read_tokens_per_1k: 0.00015,
      currency: 'USD'
    },
    capabilities: {
      streaming: true,
      reasoning: true
    },
    scores: {
      writing: 0.70,
      coding: 0.75,
      reasoning: 0.95,
      math: 0.85,
      speed: 0.65,
      cost: 0.12 // Low to moderate cost
    }
  },
  'deepseek-chat': {
    id: 'deepseek-chat',
    name: 'DeepSeek Chat',
    provider: 'deepseek',
    type: ['Basic'],
    icon: '/provider-icons/deepseek-logo-transparent.webp',
    description: 'General-purpose chat model with good performance. 50% discount during UTC 16:30-00:30',
    maxTokens: 32000,
    pricing: {
      prompt_tokens_per_1k: 0.0002,
      completion_tokens_per_1k: 0.0004,
      cached_write_tokens_per_1k: 0.0002,
      cached_read_tokens_per_1k: 0.00002,
      currency: 'USD'
    },
    capabilities: {
      streaming: true
    },
    scores: {
      writing: 0.75,
      coding: 0.70,
      reasoning: 0.70,
      math: 0.65,
      speed: 0.80,
      cost: 0.06 // Very low cost
    }
  },

  // Qwen Models
  'qwen-max': {
    id: 'qwen-max',
    name: 'Qwen Max',
    provider: 'qwen',
    type: ['Expert'],
    icon: '/provider-icons/qwen-logo.png',
    description: 'Most capable Qwen model with strong performance across tasks',
    maxTokens: 32768,
    pricing: {
      prompt_tokens_per_1k: 0.002,
      completion_tokens_per_1k: 0.002,
      currency: 'USD'
    },
    capabilities: {
      streaming: true,
      functionCalling: true,
      vision: true
    },
    scores: {
      writing: 0.85,
      coding: 0.80,
      reasoning: 0.85,
      math: 0.80,
      speed: 0.75,
      cost: 0.25 // Moderate cost
    }
  },

  // xAI Models
  'grok-2-latest': {
    id: 'grok-2-latest',
    name: 'Grok 2',
    provider: 'xai',
    type: ['Expert'],
    icon: '/provider-icons/xai-logo.webp',
    description: 'Latest version of Grok with enhanced capabilities',
    maxTokens: 128000,
    pricing: {
      prompt_tokens_per_1k: 0.002,
      completion_tokens_per_1k: 0.01,
      currency: 'USD'
    },
    capabilities: {
      streaming: true,
      functionCalling: true,
      vision: true
    },
    scores: {
      writing: 0.85,
      coding: 0.85,
      reasoning: 0.80,
      math: 0.80,
      speed: 0.80,
      cost: 0.20 // Moderate cost
    }
  },
  'grok-2-vision-latest': {
    id: 'grok-2-vision-latest',
    name: 'Grok 2 Vision',
    provider: 'xai',
    type: ['Expert'],
    icon: '/provider-icons/xai-logo.webp',
    description: 'Vision-capable version of Grok 2 for image analysis',
    maxTokens: 128000,
    pricing: {
      prompt_tokens_per_1k: 0.002,
      completion_tokens_per_1k: 0.01,
      currency: 'USD'
    },
    capabilities: {
      streaming: true,
      functionCalling: true,
      vision: true
    },
    scores: {
      writing: 0.80,
      coding: 0.80,
      reasoning: 0.80,
      math: 0.80,
      speed: 0.80,
      cost: 0.20 // Moderate cost
    }
  },

  // Groq Models
  'llama-3.3-70b-versatile': {
    id: 'llama-3.3-70b-versatile',
    name: 'LLaMA 3.3 70B Versatile',
    provider: 'groq',
    type: ['Expert'],
    icon: '/provider-icons/groq-logo-transparent.webp',
    description: 'Latest LLaMA model optimized for versatile tasks with exceptional performance',
    maxTokens: 128000,
    maxOutputTokens: 32768,
    pricing: {
      prompt_tokens_per_1k: 0.0005,
      completion_tokens_per_1k: 0.0005,
      currency: 'USD'
    },
    capabilities: {
      streaming: true,
      functionCalling: true,
      reasoning: true
    },
    scores: {
      writing: 0.90,
      coding: 0.85,
      reasoning: 0.90,
      math: 0.85,
      speed: 0.80,
      cost: 0.10 // Low cost
    }
  },
  'llama-3.1-8b-instant': {
    id: 'llama-3.1-8b-instant',
    name: 'LLaMA 3.1 8B Instant',
    provider: 'groq',
    type: ['Fast'],
    icon: '/provider-icons/groq-logo-transparent.webp',
    description: 'Fast and efficient LLaMA model optimized for quick responses',
    maxTokens: 128000,
    maxOutputTokens: 8192,
    pricing: {
      prompt_tokens_per_1k: 0.0001,
      completion_tokens_per_1k: 0.0001,
      currency: 'USD'
    },
    capabilities: {
      streaming: true,
      functionCalling: true
    },
    scores: {
      writing: 0.75,
      coding: 0.70,
      reasoning: 0.65,
      math: 0.60,
      speed: 0.90,
      cost: 0.05 // Very low cost
    }
  },
  'gemma2-9b-it': {
    id: 'gemma2-9b-it',
    name: 'Gemma2 9B',
    provider: 'groq',
    type: ['Medium', 'Fast'],
    icon: '/provider-icons/groq-logo-transparent.webp',
    description: 'Fast and efficient Gemma2 model optimized for quick responses',
    maxTokens: 8192,
    maxOutputTokens: 8192,
    pricing: {
      prompt_tokens_per_1k: 0.0001,
      completion_tokens_per_1k: 0.0001,
      currency: 'USD'
    },
    capabilities: {
      streaming: true,
      functionCalling: true
    },
    scores: {
      writing: 0.75,
      coding: 0.70,
      reasoning: 0.70,
      math: 0.65,
      speed: 0.85,
      cost: 0.05 // Very low cost
    }
  },
  'deepseek-r1-distill-llama-70b': {
    id: 'deepseek-r1-distill-llama-70b',
    name: 'DeepSeek R1 Distill LLaMA 70B',
    provider: 'groq',
    type: ['Expert'],
    icon: '/provider-icons/groq-logo-transparent.webp',
    description: 'Specialized model for complex reasoning and problem-solving tasks',
    maxTokens: 128000,
    maxOutputTokens: 16384,
    pricing: {
      prompt_tokens_per_1k: 0.0005,
      completion_tokens_per_1k: 0.0005,
      currency: 'USD'
    },
    capabilities: {
      streaming: true,
      functionCalling: true,
      reasoning: true
    },
    scores: {
      writing: 0.80,
      coding: 0.80,
      reasoning: 0.90,
      math: 0.85,
      speed: 0.75,
      cost: 0.10 // Low cost
    }
  },

  // Minimax Models
  'MiniMax-Text-01': {
    id: 'MiniMax-Text-01',
    name: 'MiniMax Text',
    provider: 'minimax',
    type: ['Medium'],
    icon: '/provider-icons/minimax-logo.jpg',
    description: 'General-purpose text generation model with balanced performance',
    maxTokens: 32000,
    pricing: {
      prompt_tokens_per_1k: 0.0002,
      completion_tokens_per_1k: 0.0011,
      currency: 'USD'
    },
    capabilities: {
      streaming: true,
      functionCalling: true
    },
    scores: {
      writing: 0.80,
      coding: 0.75,
      reasoning: 0.75,
      math: 0.70,
      speed: 0.80,
      cost: 0.10 // Low cost
    }
  },
  'minimax-image-01': {
    id: 'minimax-image-01',
    name: 'MiniMax Image',
    provider: 'minimax',
    type: ['Medium'],
    icon: '/provider-icons/minimax-logo.jpg',
    description: 'Image generation model with high-quality results',
    maxTokens: 4000,
    pricing: {
      prompt_tokens_per_1k: 0.0035, // Flat $0.0035 per image, using token pricing model for compatibility
      completion_tokens_per_1k: 0.0035,
      currency: 'USD'
    },
    capabilities: {
      streaming: false,
      functionCalling: false
    },
    scores: {
      writing: 0.0,
      coding: 0.0,
      reasoning: 0.0,
      math: 0.0,
      speed: 0.70,
      cost: 0.15 // Low to moderate cost
    }
  },
  'abab6.5s-chat': {
    id: 'abab6.5s-chat',
    name: 'ABAB 6.5S Chat',
    provider: 'minimax',
    type: ['Basic', 'Fast'],
    icon: '/provider-icons/minimax-logo.jpg',
    description: 'Fast and efficient chat model optimized for quick responses',
    maxTokens: 32000,
    pricing: {
      prompt_tokens_per_1k: 0.0002,
      completion_tokens_per_1k: 0.0002,
      currency: 'USD'
    },
    capabilities: {
      streaming: true
    },
    scores: {
      writing: 0.75,
      coding: 0.70,
      reasoning: 0.65,
      math: 0.60,
      speed: 0.85,
      cost: 0.05 // Very low cost
    }
  }
};

// Cerebras Models
export const CEREBRAS_MODELS: Record<string, ModelInfo> = {
  'llama3.1-8b': {
    id: 'llama3.1-8b',
    name: 'LLaMA 3.1 8B',
    provider: 'cerebras',
    type: ['Fast'],
    icon: '/provider-icons/cerebras-logo.png',
    description: 'Fast and efficient LLaMA model optimized for quick responses',
    maxTokens: 32768,
    pricing: {
      prompt_tokens_per_1k: 0.0001,
      completion_tokens_per_1k: 0.0001,
      currency: 'USD'
    },
    capabilities: {
      streaming: true,
      functionCalling: true
    },
    scores: {
      writing: 0.75,
      coding: 0.70,
      reasoning: 0.65,
      math: 0.60,
      speed: 0.90,
      cost: 0.05 // Very low cost
    }
  },
  'llama-3.3-70b': {
    id: 'llama-3.3-70b',
    name: 'LLaMA 3.3 70B',
    provider: 'cerebras',
    type: ['Expert'],
    icon: '/provider-icons/cerebras-logo.png',
    description: 'Latest LLaMA model with exceptional performance and advanced capabilities',
    maxTokens: 32768,
    pricing: {
      prompt_tokens_per_1k: 0.00085,
      completion_tokens_per_1k: 0.0012,
      currency: 'USD'
    },
    capabilities: {
      streaming: true,
      functionCalling: true,
      reasoning: true
    },
    scores: {
      writing: 0.90,
      coding: 0.85,
      reasoning: 0.90,
      math: 0.85,
      speed: 0.75,
      cost: 0.15 // Low to moderate cost
    }
  }
};

// Merge all models into MODELS_DATABASE
Object.assign(MODELS_DATABASE, CEREBRAS_MODELS);

// Helper functions
export function getModelInfo(modelId: string): ModelInfo | undefined {
  return MODELS_DATABASE[modelId];
}

export function getModelsByProvider(provider: string): ModelInfo[] {
  return Object.values(MODELS_DATABASE).filter(model => model.provider === provider);
}

export function getModelsByCapability(capability: keyof ModelInfo['capabilities']): ModelInfo[] {
  return Object.values(MODELS_DATABASE).filter(model => model.capabilities[capability]);
}

export function getModelsByType(type: ModelType): ModelInfo[] {
  return Object.values(MODELS_DATABASE).filter(model => model.type.includes(type));
}

// Price calculation helpers
export function calculatePromptCost(modelId: string, tokenCount: number): number {
  const model = MODELS_DATABASE[modelId];
  if (!model) return 0;
  
  // Apply DeepSeek UTC discount if applicable
  if (model.provider === 'deepseek') {
    const now = new Date();
    const utcHour = now.getUTCHours();
    const utcMinutes = now.getUTCMinutes();
    const currentTimeInMinutes = utcHour * 60 + utcMinutes;
    
    // Check if current time is within DeepSeek's discount period (UTC 16:30-00:30)
    const discountStartMinutes = 16 * 60 + 30; // 16:30 UTC
    const discountEndMinutes = 24 * 60 + 30;   // 00:30 UTC (next day)
    
    const isDiscountTime = 
      (currentTimeInMinutes >= discountStartMinutes) || // After 16:30
      (currentTimeInMinutes <= 30); // Before 00:30
    
    if (isDiscountTime) {
      // Apply model-specific discount
      if (modelId === 'deepseek-chat') {
        // 50% discount for DeepSeek Chat
        return (model.pricing.prompt_tokens_per_1k * 0.5 * tokenCount) / 1000;
      } else if (modelId === 'deepseek-reasoner') {
        // 75% discount for DeepSeek Reasoner
        return (model.pricing.prompt_tokens_per_1k * 0.25 * tokenCount) / 1000;
      }
    }
  }
  
  return (model.pricing.prompt_tokens_per_1k * tokenCount) / 1000;
}

export function calculateCompletionCost(modelId: string, tokenCount: number): number {
  const model = MODELS_DATABASE[modelId];
  if (!model) return 0;
  
  // Apply DeepSeek UTC discount if applicable
  if (model.provider === 'deepseek') {
    const now = new Date();
    const utcHour = now.getUTCHours();
    const utcMinutes = now.getUTCMinutes();
    const currentTimeInMinutes = utcHour * 60 + utcMinutes;
    
    // Check if current time is within DeepSeek's discount period (UTC 16:30-00:30)
    const discountStartMinutes = 16 * 60 + 30; // 16:30 UTC
    const discountEndMinutes = 24 * 60 + 30;   // 00:30 UTC (next day)
    
    const isDiscountTime = 
      (currentTimeInMinutes >= discountStartMinutes) || // After 16:30
      (currentTimeInMinutes <= 30); // Before 00:30
    
    if (isDiscountTime) {
      // Apply model-specific discount
      if (modelId === 'deepseek-chat') {
        // 50% discount for DeepSeek Chat
        return (model.pricing.completion_tokens_per_1k * 0.5 * tokenCount) / 1000;
      } else if (modelId === 'deepseek-reasoner') {
        // 75% discount for DeepSeek Reasoner
        return (model.pricing.completion_tokens_per_1k * 0.25 * tokenCount) / 1000;
      }
    }
  }
  
  return (model.pricing.completion_tokens_per_1k * tokenCount) / 1000;
}

// Type conversion helper
export function convertLegacyType(type: string): ModelType {
  const typeMapping: Record<string, ModelType> = {
    'Fast': 'Basic',
    'Essential': 'Basic',
    'Advanced': 'Medium',
    'Vision': 'Medium',
    'Tool': 'Medium',
    'Audio': 'Medium',
    'Security': 'Expert',
    'Reasoning': 'Expert',
    'Experimental': 'Expert'
  };

  return typeMapping[type] || 'Medium';
}

// Provider helper functions
export function getProviderConfig(provider: string): ProviderConfig | undefined {
  return PROVIDER_CONFIGS[provider];
}

export function getProviderEndpoint(provider: string, type: ProviderEndpoint): string | undefined {
  const config = PROVIDER_CONFIGS[provider];
  return config?.endpoints?.[type];
}

export function getProviderUrl(provider: string, endpoint?: string): string {
  const config = PROVIDER_CONFIGS[provider];
  if (!config) throw new Error(`No configuration found for provider: ${provider}`);
  return endpoint ? `${config.baseUrl}${endpoint}` : config.baseUrl;
}

// Token limit helpers
export function getMaxPromptTokens(modelId: string): number {
  const model = MODELS_DATABASE[modelId];
  if (!model) return 0;
  // For OpenAI models, the context window is split between prompt and completion
  if (model.provider === 'openai') {
    return model.maxTokens || 0;
  }
  // For other models, use 80% of maxTokens for prompt to leave room for completion
  return Math.floor((model.maxTokens || 0) * 0.8);
}

export function getMaxCompletionTokens(modelId: string): number {
  const model = MODELS_DATABASE[modelId];
  if (!model) return 0;
  // Use maxOutputTokens if available
  if (model.maxOutputTokens) {
    return model.maxOutputTokens;
  }
  // For OpenAI models without maxOutputTokens, use maxTokens
  if (model.provider === 'openai') {
    return model.maxTokens || 0;
  }
  // For other models, use 20% of maxTokens for completion
  return Math.floor((model.maxTokens || 0) * 0.2);
}

export function getTotalContextWindow(modelId: string): number {
  const model = MODELS_DATABASE[modelId];
  if (!model) return 0;
  // For OpenAI models, total context is double maxTokens since it's split
  if (model.provider === 'openai') {
    return (model.maxTokens || 0) * 2;
  }
  // For other models, return maxTokens directly
  return model.maxTokens || 0;
}

// Function to get current pricing info with any applicable discounts
export function getCurrentPricingInfo(modelId: string): {
  standard: { prompt: number, completion: number },
  current: { prompt: number, completion: number },
  discount: number | null
} {
  const model = MODELS_DATABASE[modelId];
  if (!model) {
    return {
      standard: { prompt: 0, completion: 0 },
      current: { prompt: 0, completion: 0 },
      discount: null
    };
  }
  
  // Get standard pricing
  const standardPrompt = model.pricing.prompt_tokens_per_1k;
  const standardCompletion = model.pricing.completion_tokens_per_1k;
  
  // Check for DeepSeek UTC discount
  if (model.provider === 'deepseek') {
    const now = new Date();
    const utcHour = now.getUTCHours();
    const utcMinutes = now.getUTCMinutes();
    const currentTimeInMinutes = utcHour * 60 + utcMinutes;
    
    // Check if current time is within DeepSeek's discount period (UTC 16:30-00:30)
    const discountStartMinutes = 16 * 60 + 30; // 16:30 UTC
    const discountEndMinutes = 24 * 60 + 30;   // 00:30 UTC (next day)
    
    const isDiscountTime = 
      (currentTimeInMinutes >= discountStartMinutes) || // After 16:30
      (currentTimeInMinutes <= 30); // Before 00:30
    
    if (isDiscountTime) {
      if (modelId === 'deepseek-chat') {
        // 50% discount for DeepSeek Chat during UTC 16:30-00:30
        return {
          standard: { 
            prompt: standardPrompt, 
            completion: standardCompletion 
          },
          current: { 
            prompt: standardPrompt * 0.5, 
            completion: standardCompletion * 0.5 
          },
          discount: 50 // 50% discount
        };
      } else if (modelId === 'deepseek-reasoner') {
        // 75% discount for DeepSeek Reasoner during UTC 16:30-00:30
        return {
          standard: { 
            prompt: standardPrompt, 
            completion: standardCompletion 
          },
          current: { 
            prompt: standardPrompt * 0.25, 
            completion: standardCompletion * 0.25 
          },
          discount: 75 // 75% discount
        };
      }
    }
  }
  
  // No discount applies
  return {
    standard: { prompt: standardPrompt, completion: standardCompletion },
    current: { prompt: standardPrompt, completion: standardCompletion },
    discount: null
  };
}

// Token validation helper
export function validateTokenCounts(modelId: string, promptTokens: number, desiredCompletionTokens: number): {
  isValid: boolean;
  error?: string;
  availableCompletionTokens?: number;
} {
  const model = MODELS_DATABASE[modelId];
  if (!model) {
    return {
      isValid: false,
      error: `Model ${modelId} not found`
    };
  }

  const maxPrompt = getMaxPromptTokens(modelId);
  const maxCompletion = getMaxCompletionTokens(modelId);

  if (promptTokens > maxPrompt) {
    return {
      isValid: false,
      error: `Prompt length (${promptTokens} tokens) exceeds model's maximum prompt length (${maxPrompt} tokens)`,
      availableCompletionTokens: 0
    };
  }

  if (desiredCompletionTokens > maxCompletion) {
    return {
      isValid: false,
      error: `Desired completion length (${desiredCompletionTokens} tokens) exceeds model's maximum completion length (${maxCompletion} tokens)`,
      availableCompletionTokens: maxCompletion
    };
  }

  // Check total context window
  const totalTokens = promptTokens + desiredCompletionTokens;
  const contextWindow = getTotalContextWindow(modelId);

  if (totalTokens > contextWindow) {
    // Calculate available completion tokens based on remaining context
    const availableForCompletion = Math.max(0, contextWindow - promptTokens);
    // For models with maxOutputTokens, ensure we don't exceed it
    const finalAvailable = model.maxOutputTokens ?
      Math.min(availableForCompletion, model.maxOutputTokens) :
      availableForCompletion;

    return {
      isValid: false,
      error: `Combined length (${totalTokens} tokens) exceeds model's context window (${contextWindow} tokens)`,
      availableCompletionTokens: finalAvailable
    };
  }

  // All checks passed
  // For models with maxOutputTokens, ensure we don't exceed it
  const availableForCompletion = contextWindow - promptTokens;
  const finalAvailable = model.maxOutputTokens ?
    Math.min(availableForCompletion, model.maxOutputTokens) :
    availableForCompletion;

  return {
    isValid: true,
    availableCompletionTokens: finalAvailable
  };
}